import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp,
  Users,
  Briefcase,
  DollarSign,
  FolderKanban,
  AlertTriangle,
  Target,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

const API = `/api`;

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`);
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-app-subtext dark:text-zinc-400">
          Loading dashboard...
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'New Leads Today',
      value: metrics?.leads_today || 0,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: 'Leads This Month',
      value: metrics?.leads_this_month || 0,
      icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      label: 'Active Deals',
      value: metrics?.active_deals || 0,
      icon: Briefcase,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    {
      label: 'Pipeline Value',
      value: `₹${(metrics?.pipeline_value || 0).toLocaleString('en-IN')}`,
      icon: DollarSign,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
    },
    {
      label: 'Revenue Closed (Month)',
      value: `₹${(metrics?.revenue_closed || 0).toLocaleString('en-IN')}`,
      icon: DollarSign,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      label: 'Active Projects',
      value: metrics?.active_projects || 0,
      icon: FolderKanban,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
    },
    {
      label: 'Overdue Projects',
      value: metrics?.overdue_projects || 0,
      icon: AlertTriangle,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
    },
    {
      label: 'Conversion Rate',
      value: `${metrics?.conversion_rate || 0}%`,
      icon: Target,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
  ];

  return (
    <div className="space-y-8">

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;

          return (
            <div
              key={index}
              className="bg-white dark:bg-[#121212] border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`${stat.bgColor} p-2 rounded-md`}>
                  <Icon
                    className={`w-5 h-5 ${stat.color}`}
                    strokeWidth={1.5}
                  />
                </div>
              </div>

              <p className="text-xs text-app-subtext uppercase tracking-wider font-medium mb-1">
                {stat.label}
              </p>

              <p className="text-3xl font-bold tracking-tight tabular-nums text-zinc-900 dark:text-zinc-100">
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Charts + Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Revenue Overview */}
        <div className="bg-white dark:bg-[#121212] border border-zinc-200 dark:border-zinc-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4 text-zinc-900 dark:text-zinc-100">
            Revenue Overview
          </h3>

          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={[
                { name: 'Pipeline', value: metrics?.pipeline_value || 0 },
                { name: 'Closed', value: metrics?.revenue_closed || 0 },
              ]}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(113,113,122,0.2)"
              />
              <XAxis
                dataKey="name"
                stroke="#71717A"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#71717A"
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181b',
                  border: '1px solid #27272A',
                  borderRadius: '8px',
                }}
              />
              <Bar
                dataKey="value"
                fill="#3B82F6"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Activity Summary */}
        <div className="bg-white dark:bg-[#121212] border border-zinc-200 dark:border-zinc-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4 text-zinc-900 dark:text-zinc-100">
            Activity Summary
          </h3>

          <div className="space-y-4">

            <div className="flex items-center justify-between p-4 bg-zinc-100 dark:bg-app-muted rounded-md border border-zinc-200 dark:border-zinc-800/50">
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                Total Active Items
              </span>
              <span className="text-lg font-bold tabular-nums">
                {(metrics?.active_deals || 0) +
                  (metrics?.active_projects || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-zinc-100 dark:bg-app-muted rounded-md border border-zinc-200 dark:border-zinc-800/50">
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                Leads Converted
              </span>
              <span className="text-lg font-bold tabular-nums">
                {metrics?.conversion_rate || 0}%
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-zinc-100 dark:bg-app-muted rounded-md border border-zinc-200 dark:border-zinc-800/50">
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                Items Needing Attention
              </span>
              <span className="text-lg font-bold tabular-nums text-red-500">
                {metrics?.overdue_projects || 0}
              </span>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;